import { useTranslation } from "react-i18next";
import HomeIcon from "@mui/icons-material/Home";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import React, { useState, useEffect, useRef } from "react";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CircularProgress from "@mui/material/CircularProgress";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { DeliveryOptionsWidgetProps } from "../../../@types/DTO/Booking";

export const DeliveryOptionsWidget: React.FC<DeliveryOptionsWidgetProps> = ({
  homeDeliverySelected,
  homeReturnSelected,
  homeDeliveryAddress,
  homeReturnAddress,
  onHomeDeliveryToggle,
  onHomeReturnToggle,
  onHomeDeliveryAddressChange,
  onHomeReturnAddressChange,
  onDeliveryCoordinatesChange,
  onReturnCoordinatesChange,
  primaryColor,
  secondaryColor,
  closestDeliveryText,
  closestReturnText,
}) => {
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const inputBorderRadius = theme.shape.borderRadius;

  // Internal state for immediate UI response — synced from parent for external resets
  const [localDeliverySelected, setLocalDeliverySelected] =
    useState(homeDeliverySelected);
  const [localReturnSelected, setLocalReturnSelected] =
    useState(homeReturnSelected);

  useEffect(() => {
    setLocalDeliverySelected(homeDeliverySelected);
  }, [homeDeliverySelected]);

  useEffect(() => {
    setLocalReturnSelected(homeReturnSelected);
  }, [homeReturnSelected]);

  // Refs point to the underlying <input> DOM node — required for Google Autocomplete.
  // With MUI TextField, pass these via the `inputRef` prop (not `ref`).
  const deliveryInputRef = useRef<HTMLInputElement | null>(null);
  const returnInputRef = useRef<HTMLInputElement | null>(null);

  // Initialize Google Autocomplete for the Home Delivery field
  useEffect(() => {
    if (
      localDeliverySelected &&
      deliveryInputRef.current &&
      window.google?.maps?.places
    ) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        deliveryInputRef.current,
        { types: ["address"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (place.formatted_address) {
          onHomeDeliveryAddressChange(place.formatted_address);
        }

        if (place.geometry?.location && onDeliveryCoordinatesChange) {
          onDeliveryCoordinatesChange({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      });
    } else if (!localDeliverySelected && onDeliveryCoordinatesChange) {
      onDeliveryCoordinatesChange(null);
    }
  }, [localDeliverySelected]);

  // Initialize Google Autocomplete for the Home Return field
  useEffect(() => {
    if (
      localReturnSelected &&
      returnInputRef.current &&
      window.google?.maps?.places
    ) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        returnInputRef.current,
        { types: ["address"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (place.formatted_address) {
          onHomeReturnAddressChange(place.formatted_address);
        }

        if (place.geometry?.location && onReturnCoordinatesChange) {
          onReturnCoordinatesChange({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      });
    } else if (!localReturnSelected && onReturnCoordinatesChange) {
      onReturnCoordinatesChange(null);
    }
  }, [localReturnSelected]);

  // Geolocation with Google Reverse Geocoding fallback
  const handleGeolocation = (
    fieldKey: "delivery" | "return",
    updateFn: (val: string) => void,
    coordFn?: (coords: { lat: number; lng: number } | null) => void
  ) => {
    if (!navigator.geolocation) return;
    setLoading((prev) => ({ ...prev, [fieldKey]: true }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              updateFn(results[0].formatted_address);
              if (coordFn) coordFn({ lat, lng });
            } else {
              updateFn(`Lat: ${lat.toFixed(4)}, Lon: ${lng.toFixed(4)}`);
            }
            setLoading((prev) => ({ ...prev, [fieldKey]: false }));
          });
        } else {
          updateFn(`Lat: ${lat.toFixed(4)}, Lon: ${lng.toFixed(4)}`);
          setLoading((prev) => ({ ...prev, [fieldKey]: false }));
        }
      },
      () => {
        setLoading((prev) => ({ ...prev, [fieldKey]: false }));
      }
    );
  };

  const isOnlyOneSelected =
    (localDeliverySelected && !localReturnSelected) ||
    (!localDeliverySelected && localReturnSelected);

  return (
    <div className="w-full flex flex-col gap-4">
      {/* BADGE TOGGLE HEADER GRIDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* HOME DELIVERY BADGE BUTTON */}
        <div
          onClick={() => {
            const newVal = !localDeliverySelected;
            setLocalDeliverySelected(newVal);
            onHomeDeliveryToggle(newVal);
          }}
          className="flex items-center justify-between px-3 cursor-pointer transition-all select-none min-h-[56px]"
          style={{
            border: `1px solid ${
              (localDeliverySelected && primaryColor) || "#D1D5DB"
            }`,
            borderRadius: inputBorderRadius,
            boxShadow: localDeliverySelected
              ? `inset 0 0 0 1px ${primaryColor || "#D1D5DB"}`
              : "none",
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Checkbox
              size="small"
              checked={localDeliverySelected}
              onChange={() => {}}
              sx={{
                p: 0,
                color: secondaryColor,
                "&.Mui-checked": { color: primaryColor || secondaryColor },
              }}
            />
            <HomeIcon
              fontSize="small"
              style={{
                color:
                  (localDeliverySelected && primaryColor) || secondaryColor,
              }}
            />
            <p className="text-sm font-normal text-gray-400 truncate">
              {translate("Home Delivery")}
            </p>
          </div>
          <p className="text-xs text-gray-400 truncate ml-2">
            {translate("Vehicle delivered to you")}
          </p>
        </div>

        {/* HOME RETURN BADGE BUTTON */}
        <div
          onClick={() => {
            const newVal = !localReturnSelected;
            setLocalReturnSelected(newVal);
            onHomeReturnToggle(newVal);
          }}
          className="flex items-center justify-between px-3 cursor-pointer transition-all select-none min-h-[56px]"
          style={{
            border: `1px solid ${
              localReturnSelected ? primaryColor : "#D1D5DB"
            }`,
            borderRadius: inputBorderRadius,
            // Inset shadow so the selected state doesn't grow the box (no layout jump).
            boxShadow: localReturnSelected
              ? `inset 0 0 0 1px ${primaryColor || "#D1D5DB"}`
              : "none",
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Checkbox
              size="small"
              checked={localReturnSelected}
              onChange={() => {}}
              sx={{
                p: 0,
                color: secondaryColor,
                "&.Mui-checked": { color: primaryColor || secondaryColor },
              }}
            />
            <KeyboardReturnIcon
              fontSize="small"
              style={{
                color: (localReturnSelected && primaryColor) || secondaryColor,
              }}
            />
            <p className="text-sm font-normal text-gray-400 truncate">
              {translate("Home Return")}
            </p>
          </div>
          <p className="text-xs text-gray-400 truncate ml-2">
            {translate("We collect from you")}
          </p>
        </div>
      </div>

      {/* ADDRESS FIELDS */}
      {(localDeliverySelected || localReturnSelected) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
          {/* DELIVERY ADDRESS — MUI TextField */}
          {localDeliverySelected && (
            <div
              className={`w-full ${isOnlyOneSelected ? "md:col-span-2" : ""}`}
            >
              <TextField
                fullWidth
                label={translate("Delivery address")}
                placeholder={translate("Search street, city...")}
                value={homeDeliveryAddress}
                onChange={(e) => onHomeDeliveryAddressChange(e.target.value)}
                // inputRef connects Google Autocomplete to the real <input> node
                inputRef={deliveryInputRef}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        size="small"
                        onClick={() =>
                          handleGeolocation(
                            "delivery",
                            onHomeDeliveryAddressChange,
                            onDeliveryCoordinatesChange
                          )
                        }
                      >
                        {loading["delivery"] ? (
                          <CircularProgress
                            size={18}
                            style={{ color: primaryColor || secondaryColor }}
                          />
                        ) : (
                          <MyLocationIcon
                            fontSize="small"
                            style={{ color: primaryColor || secondaryColor }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}

          {/* RETURN ADDRESS — MUI TextField */}
          {localReturnSelected && (
            <div
              className={`w-full ${isOnlyOneSelected ? "md:col-span-2" : ""}`}
            >
              <TextField
                fullWidth
                label={translate("Return address")}
                placeholder={translate("Search street, city...")}
                value={homeReturnAddress}
                onChange={(e) => onHomeReturnAddressChange(e.target.value)}
                // inputRef connects Google Autocomplete to the real <input> node
                inputRef={returnInputRef}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        size="small"
                        onClick={() =>
                          handleGeolocation(
                            "return",
                            onHomeReturnAddressChange,
                            onReturnCoordinatesChange
                          )
                        }
                      >
                        {loading["return"] ? (
                          <CircularProgress
                            size={18}
                            style={{ color: primaryColor || secondaryColor }}
                          />
                        ) : (
                          <MyLocationIcon
                            fontSize="small"
                            style={{ color: primaryColor || secondaryColor }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* CLOSEST BRANCH BADGES */}
      <div className="flex justify-between gap-4">
        {localDeliverySelected && closestDeliveryText && (
          <div className="flex flex-col items-start gap-2 flex-1">
            <div className="mt-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs font-semibold text-blue-800">
              📍 {closestDeliveryText}
            </div>
            <div className="w-full flex items-start gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-xs leading-relaxed text-amber-800 shadow-sm">
              <InfoOutlinedIcon
                className="flex-shrink-0"
                style={{ color: "#d97706", fontSize: "1.2rem" }}
              />
              <span className="font-black">
                {translate(
                  "A delivery surcharge will apply. The exact amount will be shown in the next step."
                )}
              </span>
            </div>
          </div>
        )}

        {localReturnSelected && closestReturnText && (
          <div className="flex flex-col items-start gap-2 flex-1">
            <div className="mt-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs font-semibold text-blue-800">
              📍 {closestReturnText}
            </div>
            <div className="w-full flex items-start gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-xs leading-relaxed text-amber-800 shadow-sm">
              <InfoOutlinedIcon
                className="flex-shrink-0"
                style={{ color: "#d97706", fontSize: "1.2rem" }}
              />
              <span className="font-black">
                {translate(
                  "A delivery surcharge will apply. The exact amount will be shown in the next step."
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryOptionsWidget;
